import {Component, Inject, OnDestroy, OnInit, Optional} from '@angular/core';
import {MAT_DIALOG_DATA, MatDialog, MatDialogRef} from '@angular/material/dialog';
import {FeeService} from '../../../services/fee/fee.service';
import {
  CommissionFee, CommissionFeeGet,
  CommissionFeePeriodPrice,
  CommissionFeePeriodPricePost, CommissionFeePeriodPriceWithLevel,
  CommissionLevel,
  Period, TranslationValue
} from '../../../generated/sales-terminal-client';
import {finalize, mergeMap} from 'rxjs/operators';
import {AuthService} from '../../../services/auth/auth.service';
import {DatePickerConfig} from '../../datepicker/datepicker.component';
import moment from 'moment-timezone';
import {cloneDeep} from 'lodash-es';
import {SortedCommissionFee} from '../commission-fees/commission-fees.component';
import {ViewService} from '../../../services/view/view.service';
import {TextInputType} from '../../forms/text-input/text-input.component';
import {SnackService} from '../../../services/snack/snack.service';
import {TranslateService} from '@ngx-translate/core';
import {Subscription} from 'rxjs';
import {PendingChangesDialogComponent} from '../../pending-changes-dialog/pending-changes-dialog.component';
import {CONTINUE_WITHOUT_SAVING} from '../../../modules/sales-terminal/sales-terminal.constants';
import {UtilService} from '../../../services/util/util.service';
import {ValidationService} from "../../../services/validation/validation.service";

interface ActiveFeePeriodPrice {
  id?: number;
  feeValue?: number;
  feeType?: 'percentage' | 'price';
  feePeriod?: Period;
}
export interface FeeDialogData {
  targetCommissionLevel: CommissionLevel,
  commissionFee: SortedCommissionFee,
  baseId: number,
  baseName: TranslationValue[] | string
}
export const COMMENT_MAX_LENGTH: number = 100;

@Component({
  selector: 'app-commission-fee-dialog',
  templateUrl: './commission-fee-dialog.component.html',
  styleUrls: ['./commission-fee-dialog.component.scss']
})
export class CommissionFeeDialogComponent implements OnInit, OnDestroy {
  public isLoading: boolean;
  public commissionFee: SortedCommissionFee;
  public targetCommissionLevel: CommissionLevel;
  public baseId: number; // Legal person or event ID
  public baseName: TranslationValue[] | string; // Legal person or event name
  public activePeriodPrice: ActiveFeePeriodPrice;
  public datePickerConfig: DatePickerConfig = {
    errorMessage: 'general.api_fees_time_invalid',
    timepicker: true, autoBindDates: true, allowDateClear: true, noHint: true, noPresets: true,
    minDate: moment.utc().toISOString()
  };
  public eventStartDatePickerConfig: DatePickerConfig = {
    errorMessage: 'general.api_fees_time_invalid',
    timepicker: true, autoBindDates: true, allowDateClear: true, noHint: true, noPresets: true,
    minDate: moment.utc(new Date(new Date().getFullYear(), new Date().getMonth(), 1)).toISOString()
  };
  public showEndDatepicker: boolean;
  public isPeriodStartValid: boolean = true;
  public isPeriodEndValid: boolean = true;
  public isPeriodCreate: boolean;
  public minEndDate: string;
  private _origComment: string;
  private _confirmSub: Subscription;

  constructor(public authService: AuthService,
              public feeService: FeeService,
              public viewService: ViewService,
              public utilService: UtilService,
              public dialogRef: MatDialogRef<CommissionFeeDialogComponent>,
              @Optional() @Inject(MAT_DIALOG_DATA) public data: FeeDialogData,
              private dialog: MatDialog,
              private snackService: SnackService,
              private translateService: TranslateService) {
    this.targetCommissionLevel = data?.targetCommissionLevel;
    this.commissionFee = this.feeService.sortFeePeriods(data?.commissionFee, this.targetCommissionLevel);
    this.baseId = data?.baseId;
    this.baseName = data?.baseName;
    this._origComment = this.commissionFee.comment;
  }

  public ngOnInit(): void {
    this.dialogRef.disableClose = true;
    this._confirmSub = this.dialogRef.backdropClick().subscribe(() => {
      this.confirmClose();
    });
  }
  public ngOnDestroy(): void {
    if (this._confirmSub) {
      this._confirmSub.unsubscribe();
      delete this._confirmSub;
    }
  }

  public saveFee(): void {
    this.isLoading = true;
    if (this.commissionFee.id) {
      if (this.commissionFee.level !== this.targetCommissionLevel) {
        this.createTargetCommissionFee();
      } else {
        this.feeService.updateCommissionFee(this.commissionFee.id, {
          comment: this.commissionFee.comment
        }).pipe(
          finalize(() => this.updateOverview())
        ).subscribe(() => {
          this.snackService.showSuccess(this.translateService.instant('general.api_fees_save_success'))
          this.dialogRef.close();
        });
      }
    } else {
      // Only possible for Super centre so no distinction required
      this.feeService.createSuperCentreCommissionFee({
        comment: this.commissionFee.comment,
        type: this.commissionFee.type,
        periodPrices: []
      }).pipe(
        finalize(() => this.updateOverview())
      ).subscribe(() => {
        this.snackService.showSuccess(this.translateService.instant('general.api_fees_create_success'));
        this.dialogRef.close();
      });
    }
  }
  public get canSaveFee(): boolean {
    return !this.activePeriodPrice && !!this.commissionFee?.sameLevelPeriodPrices?.length && (!this.commissionFee.comment?.length || this.commissionFee.comment.length <= COMMENT_MAX_LENGTH);
  }

  public addPeriodPrice(): void {
    this.activePeriodPrice = {id: null, feeType: 'percentage', feePeriod: {start: null, end: null}};
    this.isPeriodCreate = true;
  }
  public editPeriodPrice(feePeriodPrice: CommissionFeePeriodPrice): void {
    this.showEndDatepicker = !!feePeriodPrice.period?.end;
    this.activePeriodPrice = {
      id: feePeriodPrice.id,
      feeType: feePeriodPrice.feePrice ? 'price' : 'percentage',
      feePeriod: cloneDeep(feePeriodPrice.period),
      feeValue: feePeriodPrice.feePrice ? (feePeriodPrice.feePrice/100) : feePeriodPrice.feePercent
    };
  }
  public removePeriodPrice(periodPriceId: number): void {
    this.isLoading = true;
    this.feeService.removeCommissionFeePeriodPrice(this.commissionFee.id, periodPriceId).pipe(
      finalize(() => this.updateOverview())
    ).subscribe(() => this.snackService.showSuccess(this.translateService.instant('general.api_fees_period_remove_success')));
  }

  private createTargetCommissionFee(): void {
    if (this.targetCommissionLevel === CommissionLevel.Legal) {
      this.createLegalPersonCommissionFee();
    } else {
      this.createEventCommissionFee();
    }
  }
  private createLegalPersonCommissionFee(): void {
    this.feeService.createLegalPersonCommissionFee(this.baseId, {
      comment: this.commissionFee.comment,
      type: this.commissionFee.type,
      periodPrices: this.commissionFee.periodPrices
    }).pipe(
      finalize(() => this.updateOverview())
    ).subscribe(() => {
      this.snackService.showSuccess(this.translateService.instant('general.api_fees_create_success'));
      this.dialogRef.close();
    });
  }
  private createEventCommissionFee(): void {
    this.feeService.createEventCommissionFee(this.baseId, {
      comment: this.commissionFee.comment,
      type: this.commissionFee.type,
      periodPrices: this.commissionFee.periodPrices
    }).pipe(
      finalize(() => this.updateOverview())
    ).subscribe(() => {
      this.snackService.showSuccess(this.translateService.instant('general.api_fees_create_success'));
      this.dialogRef.close();
    });
  }

  public clearActivePeriodPrice(): void {
    this.activePeriodPrice = null;
    this.showEndDatepicker = false;
    this.isPeriodStartValid = true;
    this.isPeriodEndValid = true;
    this.isPeriodCreate = false;
  }

  public savePeriodPrice(): void {
    this.isLoading = true;
    const periodPricePost: CommissionFeePeriodPricePost = {
      period: this.activePeriodPrice.feePeriod,
      feePrice: this.activePeriodPrice.feeType === 'price' ? (Math.round(this.activePeriodPrice.feeValue*100)) : null,
      feePercent: this.activePeriodPrice.feeType === 'percentage' ? this.activePeriodPrice.feeValue : null
    };
    if (this.commissionFee.level !== this.targetCommissionLevel) {
      if (this.targetCommissionLevel === CommissionLevel.Legal) {
        this.feeService.createLegalPersonCommissionFee(this.baseId, {
          comment: this.commissionFee.comment,
          type: this.commissionFee.type,
          periodPrices: []
        }).pipe(
          mergeMap((createdCommissionFee: CommissionFee) =>
            this.feeService.addCommissionFeePeriodPrice(createdCommissionFee.id, periodPricePost)),
          finalize(() => this.updateOverview(true))
        ).subscribe(() => this.snackService.showSuccess(this.translateService.instant('general.api_fees_period_save_success')));
      } else {
        this.feeService.createEventCommissionFee(this.baseId, {
          comment: this.commissionFee.comment,
          type: this.commissionFee.type,
          periodPrices: []
        }).pipe(
          mergeMap((createdCommissionFee: CommissionFee) =>
            this.feeService.addCommissionFeePeriodPrice(createdCommissionFee.id, periodPricePost)),
          finalize(() => this.updateOverview(true))
        ).subscribe(() => this.snackService.showSuccess(this.translateService.instant('general.api_fees_period_save_success')));
      }
    } else {
      if (this.commissionFee.id) {
        if (this.activePeriodPrice.id) {
          this.feeService.updateCommissionFeePeriodPrice(this.commissionFee.id, this.activePeriodPrice.id, periodPricePost).pipe(
            finalize(() => this.updateOverview(true))
          ).subscribe(() => this.snackService.showSuccess(this.translateService.instant('general.api_fees_period_save_success')));
        } else {
          this.feeService.addCommissionFeePeriodPrice(this.commissionFee.id, periodPricePost).pipe(
            finalize(() => this.updateOverview(true))
          ).subscribe(() => this.snackService.showSuccess(this.translateService.instant('general.api_fees_period_save_success')));
        }
      } else {
        this.feeService.createSuperCentreCommissionFee({
          comment: this.commissionFee.comment,
          type: this.commissionFee.type,
          periodPrices: [periodPricePost]
        }).pipe(
          finalize(() => this.updateOverview(true))
        ).subscribe(() => this.snackService.showSuccess(this.translateService.instant('general.api_fees_period_save_success')));
      }
    }
  }
  private updateOverview(clearPeriodPrice?: boolean): void {
    this.isLoading = true;
    switch (this.targetCommissionLevel) {
      case CommissionLevel.SuperCentre:
        this.feeService.getSuperCentreCommissionFees().pipe(
          finalize(() => this.finalizeRequest(clearPeriodPrice))
        ).subscribe((results: CommissionFeeGet[]) => this.prepareCommissionFeeResult(results));
        break;
      case CommissionLevel.Legal:
        this.feeService.getLegalPersonCommissionFees(this.baseId).pipe(
          finalize(() => this.finalizeRequest(clearPeriodPrice))
        ).subscribe((results: CommissionFeeGet[]) => this.prepareCommissionFeeResult(results));
        break;
      case CommissionLevel.Event:
        this.feeService.getEventCommissionFees(this.baseId).pipe(
          finalize(() => this.finalizeRequest(clearPeriodPrice))
        ).subscribe((results: CommissionFeeGet[]) => this.prepareCommissionFeeResult(results));
        break;
      default:
        this.finalizeRequest(clearPeriodPrice);
        break;
    }
  }
  private prepareCommissionFeeResult(results: CommissionFeeGet[]): void {
    results.forEach((fee: CommissionFeeGet) => {
      if (fee.type === this.commissionFee.type) {
        const currentComment: string = this.commissionFee.comment;
        this.commissionFee = this.feeService.sortFeePeriods(fee, this.targetCommissionLevel);
        this.commissionFee.comment = currentComment;
      }
    });
  }
  private finalizeRequest(clearPeriodPrice?: boolean): void {
    this.isLoading = false;
    if (clearPeriodPrice) {
      this.clearActivePeriodPrice();
    }
  }

  public isActivePeriodPrice(id: number): boolean {
    return this.activePeriodPrice?.id === id;
  }

  public isCurrentPeriodPrice(id: number, start: string): boolean {
    const currentUniqueId: string = this.commissionFee.currentPeriodPrice?.id + this.commissionFee.currentPeriodPrice?.period?.start;
    const periodUniqueId: string = id + start;
    return currentUniqueId === periodUniqueId;
  }

  public validatePeriodStart(): void {
    const start: moment.Moment = moment(this.activePeriodPrice.feePeriod?.start || null);
    const now: moment.Moment = moment(new Date());
    this.isPeriodStartValid = !this.activePeriodPrice.feePeriod?.start ||
      (start.isValid() && ((this.targetCommissionLevel === CommissionLevel.Event && start.isSameOrAfter(now, 'month')) || start.isAfter(now)) &&
        !this.isDuplicatePeriodStart(start, this.activePeriodPrice.id));
    this.validatePeriodEnd();
  }
  public validatePeriodEnd(): void {
    const now: moment.Moment = moment(new Date());
    const start: moment.Moment = moment(this.activePeriodPrice.feePeriod?.start || null);
    const end: moment.Moment = moment(this.activePeriodPrice.feePeriod?.end || null);
    this.isPeriodEndValid = !this.activePeriodPrice.feePeriod?.end || (end.isValid() && end.isAfter(start) && end.isSameOrAfter(now));
    this.updateMinEndDate();
  }
  private isDuplicatePeriodStart(start: moment.Moment, id: number): boolean {
    return this.commissionFee.sameLevelPeriodPrices?.length > 0 && this.commissionFee.sameLevelPeriodPrices.filter((periodPrice: CommissionFeePeriodPriceWithLevel) =>
      periodPrice.id !== id && moment(periodPrice.period?.start || null).isSame(start)).length > 0;
  }

  public get canSavePeriodPrice(): boolean {
    return this.activePeriodPrice.feeValue && !!ValidationService.validatePercentage(this.activePeriodPrice.feeValue, true) && !!this.activePeriodPrice.feePeriod.start && this.isPeriodStartValid && this.isPeriodEndValid;
  }

  public setDefaultEndDate(): void {
    this.updateMinEndDate();
    this.activePeriodPrice.feePeriod.end = moment(this.minEndDate).set('h', 23).set('m', 59).set('s', 0).toISOString();
    this.showEndDatepicker = true;
  }

  public updateMinEndDate(): void {
    const now: moment.Moment = moment(new Date());
    const start: moment.Moment = moment(this.activePeriodPrice.feePeriod?.start || null);
    this.minEndDate = start.isBefore(now) ? now.toISOString() : start.toISOString();
  }

  public confirmClose(): void {
    if (this._origComment === this.commissionFee.comment && !this.activePeriodPrice) {
      this.close();
    } else {
      const pendingChanges: PendingChangesDialogComponent = new PendingChangesDialogComponent(this.dialog, this.translateService, {
        pendingChangesDescription: this.translateService.instant('general.api_fees_unsaved_changes'),
        canNotSave: !this.canSaveFee || (!!this.activePeriodPrice && !this.canSavePeriodPrice),
        buttonOk: !this.commissionFee.id ? this.translateService.instant('general.api_fees_create') : this.translateService.instant('general.api_fees_save')
      });
      pendingChanges.dialogRef.afterClosed().subscribe((res) => {
        if (res === true) {
          if (this.activePeriodPrice) {
            this.savePeriodPrice();
          }
          if (this._origComment !== this.commissionFee.comment) {
            this.saveFee();
          }
        } else if (res === CONTINUE_WITHOUT_SAVING) {
          this.close();
        }
      });
    }
  }
  private close(): void {
    this.dialogRef.disableClose = false;
    this.dialogRef.close();
  }

  protected readonly CommissionLevel = CommissionLevel;
  protected readonly TextInputType = TextInputType;
  protected readonly COMMENT_MAX_LENGTH = COMMENT_MAX_LENGTH;
}
